import { useState } from 'react';
import { TextField } from '@mui/material';

/**
 * Whole-number field that never silently corrects what is being typed.
 *
 * A controlled number field whose onChange clamps every keystroke into [min, max] cannot be
 * emptied — the old value comes straight back — so the next digit appends to it ("2" turns "1"
 * into "12") and the clamp pushes the result to the maximum. Staff then confirmed counts they
 * never entered. Here the raw text is kept while the field has focus: focusing selects it so
 * typing replaces the value, an entry inside the limits is handed up immediately, and anything
 * outside them is marked as an error and only clamped once on blur, in plain sight.
 *
 * Mirrors NumberField in funzone-management-frontend-new.
 */
function NumberField({ value, min, max, onValueChange, inputProps, ...props }) {
  const [draft, setDraft] = useState(null);
  const lower = typeof min === 'number' ? min : 0;
  const upper = typeof max === 'number' ? max : Number.POSITIVE_INFINITY;
  const parsed = draft !== null && draft !== '' ? Number(draft) : null;
  const isInvalid = parsed !== null && (parsed < lower || parsed > upper);

  return (
    <TextField
      {...props}
      type='number'
      value={draft ?? String(value)}
      error={props.error || isInvalid}
      inputProps={{ min, max, inputMode: 'numeric', ...(inputProps || {}) }}
      onFocus={e => {
        setDraft(String(value));
        e.target.select();
        props.onFocus?.(e);
      }}
      onChange={e => {
        const raw = e.target.value.replace(/[^0-9]/g, '');
        setDraft(raw);
        if (raw === '') return;
        const next = Number(raw);
        if (next >= lower && next <= upper && next !== value) onValueChange(next);
      }}
      onBlur={e => {
        const raw = draft;
        setDraft(null);
        if (raw !== null) {
          const next = raw === '' ? lower : Math.min(Math.max(Number(raw), lower), upper);
          if (next !== value) onValueChange(next);
        }
        props.onBlur?.(e);
      }}
    />
  );
}

export default NumberField;
